import React, { useState, useEffect, useRef } from 'react';
import { RotateCcw, Download, Upload } from 'lucide-react';
import { ReactComponent as LogoSvg } from './svgs/spheromanlogo.svg';
import DownloadModal from './DownloadModal';
import { ProjectionIcons } from './svgs';

type ProjectionType = 
  | 'EQUIRECTANGULAR' 
  | 'PILLOW' 
  | 'FLOWER' 
  | 'CUBE' 
  | 'DOMES'
  | 'TENNIS' 
  | 'BASEBALL' 
  | 'BASKETBALL';

interface ControlBarProps {
    onUpload: () => void;
    onReset: () => void;
    onExportWEBM: () => Promise<void>;
    onExportGLB: () => Promise<void>;
    onExportGLTF: () => Promise<void>;
    onExportPNG: () => Promise<void>;
    onExportHalfPNG: () => Promise<void>;
    onExportRightHalf: () => Promise<void>;
    onExportJPG: () => Promise<void>;
    onExportWEBP: () => Promise<void>;
    onExportSpherePNG: () => Promise<void>;
    onExportPeel: () => Promise<void>;
    onExportBananas: () => Promise<void>;
    onExportPeanuts: () => Promise<void>;
    hasImage: boolean;
    selectedProjection: ProjectionType;
    onProjectionChange: (projection: ProjectionType) => void;
    projectionTypes: Record<ProjectionType, string>;
    isExportingVideo: boolean;
    fileName: string;
    fileSize: string;
    imageResolution: string;
}

interface ProjectionIconProps {
  type?: ProjectionType;
  color?: string;
  width?: string | number;
  height?: string | number;
}

const ProjectionIcon = ({ type, color = "currentColor", width = 24, height = 24 }: ProjectionIconProps) => {
  const Icon = type ? (ProjectionIcons[type] || ProjectionIcons.EQUIRECTANGULAR) : ProjectionIcons.EQUIRECTANGULAR;
  return (
    <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
      <Icon style={{ opacity: 'inherit', width: '100%', height: '100%' }} />
    </div>
  );
};

const ControlBar: React.FC<ControlBarProps> = ({
    onUpload,
    onReset,
    onExportWEBM,
    onExportGLB,
    onExportGLTF,
    onExportPNG,
    onExportHalfPNG,
    onExportRightHalf,
    onExportJPG,
    onExportWEBP,
    onExportSpherePNG,
    onExportPeel,
    onExportBananas,
    onExportPeanuts,
    hasImage,
    selectedProjection,
    onProjectionChange,
    projectionTypes,
    isExportingVideo,
    fileName,
    fileSize,
    imageResolution
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);
  const [projectionMenuOpen, setProjectionMenuOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setProjectionMenuOpen(false);
      }
    };

    if (projectionMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [projectionMenuOpen]);

  const containerStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '10px',
  };

  const handleDownloadClick = () => {
    if (hasImage) {
      setIsModalOpen(true);
    }
  };

  const projectionDisplayText = hasImage 
    ? selectedProjection 
    : "PROJECTION";

  return (
    <>
      <div style={containerStyle}>
        <div style={{  
          color: '#00a000', 
          width: '303px', 
          marginBottom: '16px',
        }}>
          <LogoSvg width="100%" height="100%" />
        </div>
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '34px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button 
                onClick={onUpload}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  display: 'flex',
                  padding: 0,
                  marginRight: '8px'
                }}
              >
                <Upload size={24} color="#00a000" />
              </button>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                overflow: 'hidden'
              }}>
                {!fileName ? (
                  <span style={{ 
                    fontSize: '16px',
                    color: '#bbb',
                    fontWeight: 'normal',
                    letterSpacing: '0.05em'
                  }}>360° IMAGE</span>
                ) : (
                  <div>
                    <span style={{ 
                      fontSize: '16px',
                      color: imageResolution.includes('exceeds') ? '#ff4444' : '#bbb',
                      fontWeight: 'normal',
                      letterSpacing: '0.07em',
                      display: 'block'
                    }}>
                      {imageResolution.includes('exceeds') ? imageResolution : `${fileSize}  ${imageResolution.replace(/x(\d+)$/, 'x$1PX')}`}
                    </span>
                    <span style={{ 
                      fontSize: '16px',
                      color: imageResolution.includes('exceeds') ? '#ff4444' : '#888',
                      fontWeight: 'normal',
                      display: 'block'
                    }}>
                      {fileName}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <button 
              onClick={handleDownloadClick}
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: hasImage ? 'pointer' : 'not-allowed',
                display: 'flex',
                padding: 0,
                opacity: hasImage ? 1 : 0.5
              }}
            >
              <Download size={24} color="#00a000" />
            </button>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            height: '32px',
            width: '100%',
            gap: '8px'
          }}>
            <div 
              ref={menuRef}
              style={{
                position: 'relative',
                cursor: hasImage ? 'pointer' : 'default',
                userSelect: 'none',
                color: '#bbb',
                fontWeight: 'normal',
                letterSpacing: '0.05em',
                fontSize: '15px',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onClick={() => hasImage && setProjectionMenuOpen(!projectionMenuOpen)}
            >
              <div style={{ opacity: hasImage ? 1 : 0.5 }}>
                <ProjectionIcon type={selectedProjection} color="#00a000" width={24} height={24} />
              </div>
              {projectionDisplayText}
              {projectionMenuOpen && hasImage && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  backgroundColor: '#1A1A1A',
                  border: '1px solid #333',
                  borderRadius: '4px',
                  zIndex: 1000,
                  marginTop: '4px',
                  width: '220px'
                }}>
                  {(Object.keys(projectionTypes) as ProjectionType[]).map((key) => (
                    <div 
                      key={key}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor: selectedProjection === key ? '#00a000' : 'transparent',
                        color: selectedProjection === key ? 'white' : '#bbb',
                        transition: 'all 0.2s ease',
                        fontSize: '16px',
                        letterSpacing: '0.05em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onProjectionChange(key);
                        setProjectionMenuOpen(false);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#00a000';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        if (selectedProjection !== key) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#bbb';
                        }
                      }}
                    >
                      <ProjectionIcon 
                        type={key} 
                        color={selectedProjection === key ? 'white' : '#00a000'} 
                        width={24} 
                        height={24} 
                      />
                      {key}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ width: '24px' }}>
              <button 
                onClick={hasImage ? onReset : undefined}
                onMouseEnter={() => setHoveredIcon('reset')}
                onMouseLeave={() => setHoveredIcon(null)}
                title="Reset All"
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: hasImage ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  opacity: hasImage ? 1 : 0.5,
                  color: '#00a000',
                  padding: 0
                }}
              >
                <RotateCcw size={24} strokeWidth={2} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <DownloadModal
        isOpen={isModalOpen}
        onClose={() => !isExporting && setIsModalOpen(false)}
        selectedProjection={selectedProjection}
        onExportPNG={onExportPNG}
        onExportHalfPNG={onExportHalfPNG}
        onExportRightHalf={onExportRightHalf}
        onExportJPG={onExportJPG}
        onExportWEBP={onExportWEBP}
        onExportWEBM={onExportWEBM}
        onExportGLB={onExportGLB}
        onExportGLTF={onExportGLTF}
        onExportSpherePNG={onExportSpherePNG}
        onExportPeel={onExportPeel}
        onExportBananas={onExportBananas}
        onExportPeanuts={onExportPeanuts}
        isExportingVideo={isExportingVideo}
        onExportStart={() => setIsExporting(true)}
        onExportEnd={() => setIsExporting(false)}
      />
    </>
  );
};

export default ControlBar;